/*
 * One rule for every link that comes from the panel (article text, notices, tables, landing
 * blocks) or is built in code from API data.
 *
 * A bad href does two kinds of damage:
 *  - security: "javascript:" / "vbscript:" / "data:" run code or load content when clicked;
 *  - SEO: anything that is not a full URL, a "/path", "#anchor" or "?query" is read by browsers
 *    and Google as relative to the current page, so "javascript:void(0)", "bingx/" or
 *    "www.site.com" on /basis-volume/ became /basis-volume/javascript:void(0) etc., which Google
 *    crawled and reported as 404 pages.
 *
 * safeHref returns the cleaned address, or null when the link should have no address at all.
 */

const UNSAFE_SCHEME = /^(javascript|vbscript|data|file):/i;
const ALLOWED_SCHEME = /^(https?|mailto|tel|sms|tg|whatsapp|intent):/i;
const ANY_SCHEME = /^[a-z][a-z0-9+.-]*:/i;
// "www.site.com/x", "khanesarmaye.com/x": a domain typed without https://
const BARE_DOMAIN = /^(www\.)?[a-z0-9-]+(\.[a-z0-9-]+)*\.[a-z]{2,}(?=[/?#:]|$)/i;
const PLACEHOLDER = /^(undefined|null|nan|unknown|#undefined)$|\{\{|\$\{|%7b%7b/i;

export function safeHref(raw: string | null | undefined): string | null {
    if (raw == null) return null;
    // Browsers ignore whitespace and control characters inside the scheme ("java\nscript:")
    const value = String(raw).replace(/[\u0000- \u007f]/g, '');
    if (!value) return null;

    let decoded = value;
    try {
        decoded = decodeURIComponent(value);
    } catch {
        // keep the raw value
    }
    if (UNSAFE_SCHEME.test(value) || UNSAFE_SCHEME.test(decoded)) return null;
    if (PLACEHOLDER.test(value) || PLACEHOLDER.test(decoded)) return null;

    // Already absolute or page-local
    if (ALLOWED_SCHEME.test(value)) return String(raw).trim();
    if (/^(\/|#|\?)/.test(value)) return String(raw).trim();
    // Some other scheme we do not know: no address rather than a broken one
    if (ANY_SCHEME.test(value) && !BARE_DOMAIN.test(value)) return null;

    if (BARE_DOMAIN.test(value)) return `https://${String(raw).trim()}`;
    // "../x" and "./x" resolve correctly from the page; a bare "bingx/" does not
    if (/^\.\.?\//.test(value)) return String(raw).trim();
    // A bare path like "bingx/": only when it looks like one. Text pasted into the link field
    // ("[Bookmap,", "see here") is not an address at all.
    const trimmed = String(raw).trim();
    if (!/^[\p{L}\p{N}\-._~%/+]+([?#].*)?$/u.test(trimmed)) return null;
    return `/${trimmed}`;
}

/** What the browser reads from an attribute: HTML entities decoded ("&#106;" -> "j"). */
function decodeAttr(value: string): string {
    return value
        .replace(/&#x([0-9a-f]+);?/gi, (_, h: string) => String.fromCodePoint(parseInt(h, 16)))
        .replace(/&#(\d+);?/g, (_, d: string) => String.fromCodePoint(Number(d)))
        .replace(/&colon;/gi, ':')
        .replace(/&tab;/gi, '\t')
        .replace(/&newline;/gi, '\n')
        .replace(/&quot;/gi, '"')
        .replace(/&apos;/gi, "'")
        .replace(/&lt;/gi, '<')
        .replace(/&gt;/gi, '>')
        .replace(/&amp;/gi, '&');
}

const escapeAttr = (value: string) =>
    value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');

/** The same rule on an HTML string (for markup injected as-is). Only href attributes change. */
export function safeHtmlLinks(html: string | null | undefined): string {
    if (!html) return '';
    if (!/href\s*=/i.test(html)) return html;
    return html.replace(
        /(\shref\s*=\s*)(?:"([^"]*)"|'([^']*)'|([^\s>"']+))/gi,
        (_whole, _attr: string, dq?: string, sq?: string, bare?: string) => {
            const original = dq ?? sq ?? bare ?? '';
            const fixed = safeHref(decodeAttr(original));
            if (fixed === null) return '';
            return ` href="${escapeAttr(fixed)}"`;
        },
    );
}

/** A site path built from API data ("/exchange/" + slug): null when the slug is missing. */
export function sitePath(prefix: string, slug: string | null | undefined): string | null {
    const s = (slug ?? '').toString().trim();
    if (!s || PLACEHOLDER.test(s) || /[\s<>"']/.test(s)) return null;
    return `/${prefix.replace(/^\/|\/$/g, '')}/${encodeURI(s.toLowerCase())}/`;
}
